import { useState, useCallback, useEffect, useRef } from 'react';

interface UseRefreshDelayProps {
  delaySeconds: number;
  onRefresh: () => void;
}

interface UseRefreshDelayReturn {
  isDisabled: boolean;
  remainingSeconds: number;
  handleRefresh: () => void;
}

export default function useRefreshDelay({
  delaySeconds,
  onRefresh,
}: UseRefreshDelayProps): UseRefreshDelayReturn {
  const [isDisabled, setIsDisabled] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const startTimeRef = useRef<number | null>(null);

  const handleRefresh = useCallback(() => {
    if (isDisabled) return;

    setIsDisabled(true);
    setRemainingSeconds(delaySeconds);
    startTimeRef.current = Date.now();
    onRefresh();
  }, [isDisabled, delaySeconds, onRefresh]);

  useEffect(() => {
    if (!isDisabled) return;

    const interval = setInterval(() => {
      if (!startTimeRef.current) return;

      const elapsedSeconds = Math.floor(
        (Date.now() - startTimeRef.current) / 1000,
      );
      const remaining = Math.max(0, delaySeconds - elapsedSeconds);

      if (remaining <= 0) {
        setIsDisabled(false);
        setRemainingSeconds(0);
        startTimeRef.current = null;
      } else {
        setRemainingSeconds(remaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isDisabled, delaySeconds]);

  return {
    isDisabled,
    remainingSeconds,
    handleRefresh,
  };
}
